import React, { useState, useMemo } from 'react';
import { useTheme } from '../../theme/ThemeProvider';

const DataSourceList = ({ sources, onEdit, onDelete, loading }) => {
  const { mode } = useTheme();
  const [viewMode, setViewMode] = useState('compact'); // 'compact' | 'detailed'
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const getCategoryIcon = (category) => {
    const icons = {
      'concatenados': '📦',
      'newton_results': '🎯',
      'intelligence': '🧠',
      'products': '📦',
      'publications': '📢',
      'business': '💼',
      'metadata': '📁'
    };
    return icons[category] || '🔌';
  };
  
  const getCategoryColor = (category) => {
    const colors = {
      'concatenados': '#29B5E8',
      'newton_results': '#38C172',
      'intelligence': '#FF6B35',
      'products': '#8B5CF6',
      'publications': '#F59E0B',
      'business': '#EF4444',
      'metadata': '#6B7280'
    };
    return colors[category] || '#29B5E8';
  };
  
  // Agrupar fuentes por categoría
  const groupedSources = useMemo(() => {
    const groups = {};
    sources.forEach(source => {
      const category = source.connection_config?.category || 'metadata';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(source);
    });
    return groups;
  }, [sources]);
  
  // Filtrar y buscar
  const filteredSources = useMemo(() => {
    let filtered = sources;
    
    // Filtrar por categoría
    if (filterCategory !== 'all') {
      filtered = filtered.filter(source => 
        source.connection_config?.category === filterCategory
      );
    }
    
    // Buscar por término
    if (searchTerm) {
      filtered = filtered.filter(source =>
        source.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        source.connection_config?.table?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  }, [sources, filterCategory, searchTerm]);
  
  // Obtener estadísticas
  const stats = useMemo(() => {
    const categories = Object.keys(groupedSources);
    const totalSources = sources.length;
    const totalRecords = sources.reduce((sum, source) => 
      sum + (source.connection_config?.row_count || 0), 0
    );
    
    return { categories, totalSources, totalRecords };
  }, [sources, groupedSources]);
  
  const styles = {
    // Contenedor principal
    container: {
      backgroundColor: mode === 'dark' ? '#151a21' : '#FFFFFF',
      borderRadius: '12px',
      border: `1px solid ${mode === 'dark' ? '#2d3748' : '#E2E8F0'}`,
      overflow: 'hidden'
    },
    
    // Header con controles
    header: {
      padding: '16px 24px',
      borderBottom: `1px solid ${mode === 'dark' ? '#2d3748' : '#E2E8F0'}`,
      backgroundColor: mode === 'dark' ? '#1a1f2e' : '#F5F7FA',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '12px'
    },
    
    // Estadísticas
    stats: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      fontSize: '14px',
      color: mode === 'dark' ? '#9aa8bd' : '#8B9CB5'
    },
    
    // Controles
    controls: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    
    // Filtros de categoría
    categoryFilters: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap'
    },
    
    categoryButton: {
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.15s ease'
    },
    
    categoryButtonActive: {
      backgroundColor: '#29B5E8',
      color: '#FFFFFF'
    },
    
    categoryButtonInactive: {
      backgroundColor: mode === 'dark' ? '#2d3748' : '#E2E8F0',
      color: mode === 'dark' ? '#9aa8bd' : '#8B9CB5'
    },
    
    // Buscador
    searchInput: {
      padding: '8px 12px',
      borderRadius: '8px',
      border: `1px solid ${mode === 'dark' ? '#4a5568' : '#E2E8F0'}`,
      backgroundColor: mode === 'dark' ? '#151a21' : '#FFFFFF',
      color: mode === 'dark' ? '#f6f8fb' : '#1A202C',
      fontSize: '14px',
      width: '200px',
      transition: 'border-color 0.15s ease'
    },
    
    // Toggle de vista
    viewToggle: {
      display: 'flex',
      backgroundColor: mode === 'dark' ? '#2d3748' : '#E2E8F0',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    
    viewButton: {
      padding: '8px 12px',
      border: 'none',
      backgroundColor: 'transparent',
      color: mode === 'dark' ? '#9aa8bd' : '#8B9CB5',
      fontSize: '12px',
      cursor: 'pointer',
      transition: 'all 0.15s ease'
    },
    
    viewButtonActive: {
      backgroundColor: '#29B5E8',
      color: '#FFFFFF'
    },
    
    // Lista compacta
    compactList: {
      maxHeight: '600px',
      overflowY: 'auto'
    },
    
    // Item compacto
    compactItem: {
      padding: '12px 24px',
      borderBottom: `1px solid ${mode === 'dark' ? '#2d3748' : '#E2E8F0'}`,
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      transition: 'background-color 0.15s ease',
      cursor: 'pointer'
    },
    
    compactItemHover: {
      backgroundColor: mode === 'dark' ? 'rgba(41, 181, 232, 0.05)' : 'rgba(41, 181, 232, 0.02)'
    },
    
    // Icono de categoría
    categoryIcon: {
      fontSize: '20px',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '8px',
      backgroundColor: mode === 'dark' ? '#2d3748' : '#F5F7FA'
    },
    
    // Info principal
    mainInfo: {
      flex: 1,
      minWidth: 0
    },
    
    sourceName: {
      fontSize: '14px',
      fontWeight: '600',
      color: mode === 'dark' ? '#f6f8fb' : '#1A202C',
      marginBottom: '2px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    
    sourceDetails: {
      fontSize: '12px',
      color: mode === 'dark' ? '#9aa8bd' : '#8B9CB5',
      display: 'flex',
      gap: '12px',
      alignItems: 'center'
    },
    
    // Badge de categoría
    categoryBadge: {
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '10px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    
    // Contador de registros
    recordCount: {
      fontSize: '12px',
      fontWeight: '500',
      color: mode === 'dark' ? '#38C172' : '#059669',
      backgroundColor: mode === 'dark' ? 'rgba(56, 193, 114, 0.1)' : 'rgba(5, 150, 105, 0.1)',
      padding: '2px 8px',
      borderRadius: '12px'
    },
    
    // Badge de sistema
    systemBadge: {
      backgroundColor: '#29B5E8',
      color: '#FFFFFF',
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '10px',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    
    // Acciones
    actions: {
      display: 'flex',
      gap: '8px',
      opacity: 0,
      transition: 'opacity 0.15s ease'
    },
    
    actionButton: {
      padding: '6px 8px',
      borderRadius: '6px',
      border: 'none',
      fontSize: '12px',
      cursor: 'pointer',
      transition: 'all 0.15s ease'
    },
    
    editButton: {
      backgroundColor: mode === 'dark' ? '#2d3748' : '#E2E8F0',
      color: mode === 'dark' ? '#9aa8bd' : '#8B9CB5'
    },
    
    deleteButton: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444'
    }
  };
  
  const formatRecordCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count?.toLocaleString() || '0';
  };

  return (
    <div style={styles.container}>
      {/* Header con controles */}
      <div style={styles.header}>
        <div style={styles.stats}>
          <span>📊 {stats.totalSources} fuentes</span>
          <span>•</span>
          <span>💾 {formatRecordCount(stats.totalRecords)} registros</span>
          <span>•</span>
          <span>📂 {stats.categories.length} categorías</span>
        </div>
        
        <div style={styles.controls}>
          {/* Buscador */}
          <input
            type="text"
            placeholder="Buscar fuentes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          
          {/* Filtros de categoría */}
          <div style={styles.categoryFilters}>
            <button
              style={{
                ...styles.categoryButton,
                ...(filterCategory === 'all' ? styles.categoryButtonActive : styles.categoryButtonInactive)
              }}
              onClick={() => setFilterCategory('all')}
            >
              Todas ({sources.length})
            </button>
            {stats.categories.map(category => (
              <button
                key={category}
                style={{
                  ...styles.categoryButton,
                  ...(filterCategory === category ? styles.categoryButtonActive : styles.categoryButtonInactive)
                }}
                onClick={() => setFilterCategory(category)}
              >
                {getCategoryIcon(category)} {category} ({groupedSources[category]?.length || 0})
              </button>
            ))}
          </div>
          
          {/* Toggle de vista */}
          <div style={styles.viewToggle}>
            <button
              style={{
                ...styles.viewButton,
                ...(viewMode === 'compact' ? styles.viewButtonActive : {})
              }}
              onClick={() => setViewMode('compact')}
            >
              📋 Compacta
            </button>
            <button
              style={{
                ...styles.viewButton,
                ...(viewMode === 'detailed' ? styles.viewButtonActive : {})
              }}
              onClick={() => setViewMode('detailed')}
            >
              📄 Detallada
            </button>
          </div>
        </div>
      </div>
      
      {/* Lista de fuentes */}
      {viewMode === 'compact' ? (
        <div style={styles.compactList}>
          {filteredSources.map((source, index) => {
          const category = source.connection_config?.category || 'metadata';
          const categoryColor = getCategoryColor(category);
          const recordCount = source.connection_config?.row_count || 0;
          
          return (
            <div
              key={source.id}
              style={styles.compactItem}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = mode === 'dark' ? 'rgba(41, 181, 232, 0.05)' : 'rgba(41, 181, 232, 0.02)';
                const actions = e.currentTarget.querySelector('.actions');
                if (actions) actions.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                const actions = e.currentTarget.querySelector('.actions');
                if (actions) actions.style.opacity = '0';
              }}
            >
              {/* Icono de categoría */}
              <div style={styles.categoryIcon}>
                {getCategoryIcon(category)}
              </div>
              
              {/* Información principal */}
              <div style={styles.mainInfo}>
                <div style={styles.sourceName}>{source.name}</div>
                <div style={styles.sourceDetails}>
                  <span>🗄️ {source.connection_config?.database || 'N/A'}</span>
                  <span>•</span>
                  <span>📊 {source.connection_config?.table || 'N/A'}</span>
                  {source.is_hardcoded && (
                    <>
                      <span>•</span>
                      <span style={styles.systemBadge}>Sistema</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Badge de categoría */}
              <div style={{
                ...styles.categoryBadge,
                backgroundColor: `${categoryColor}20`,
                color: categoryColor
              }}>
                {category}
              </div>
              
              {/* Contador de registros */}
              <div style={styles.recordCount}>
                {formatRecordCount(recordCount)}
              </div>
              
              {/* Acciones (aparecen en hover) */}
              {!source.is_hardcoded && (
                <div className="actions" style={styles.actions}>
                  <button
                    style={{...styles.actionButton, ...styles.editButton}}
                    onClick={() => onEdit(source)}
                    disabled={loading}
                    title="Editar fuente"
                  >
                    ✏️
                  </button>
                  <button
                    style={{...styles.actionButton, ...styles.deleteButton}}
                    onClick={() => onDelete(source.id)}
                    disabled={loading}
                    title="Eliminar fuente"
                  >
                    🗑️
                  </button>
                </div>
              )}
            </div>
          );
        })}
        
        {filteredSources.length === 0 && (
          <div style={{
            padding: '48px 24px',
            textAlign: 'center',
            color: mode === 'dark' ? '#9aa8bd' : '#8B9CB5',
            fontSize: '14px'
          }}>
            🔍 No se encontraron fuentes que coincidan con los filtros
          </div>
        )}
        </div>
      ) : (
        // Vista Detallada
        <div style={{
          padding: '24px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
          gap: '24px',
          maxHeight: '800px',
          overflowY: 'auto'
        }}>
          {filteredSources.map((source) => {
            const category = source.connection_config?.category || 'metadata';
            const categoryColor = getCategoryColor(category);
            const recordCount = source.connection_config?.row_count || 0;
            const columnCount = source.connection_config?.column_count || 0;
            
            return (
              <div
                key={source.id}
                style={{
                  backgroundColor: mode === 'dark' ? '#1a1f2e' : '#FFFFFF',
                  border: `1px solid ${mode === 'dark' ? '#2d3748' : '#E2E8F0'}`,
                  borderRadius: '12px',
                  padding: '20px',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = mode === 'dark' 
                    ? '0 8px 16px rgba(0, 0, 0, 0.3)' 
                    : '0 8px 16px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Header de card */}
                <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <div style={{
                      fontSize: '28px',
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '10px',
                      backgroundColor: `${categoryColor}20`
                    }}>
                      {getCategoryIcon(category)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: '600',
                        color: mode === 'dark' ? '#f6f8fb' : '#1A202C',
                        marginBottom: '4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {source.name}
                      </h3>
                      <div style={{
                        fontSize: '12px',
                        color: mode === 'dark' ? '#9aa8bd' : '#8B9CB5'
                      }}>
                        {source.connection_config?.database || 'N/A'} • {source.connection_config?.table || 'N/A'}
                      </div>
                    </div>
                  </div>
                  {source.is_hardcoded && (
                    <span style={styles.systemBadge}>SISTEMA</span>
                  )}
                </div>
                
                {/* Estadísticas en grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '12px',
                    backgroundColor: mode === 'dark' ? '#151a21' : '#F5F7FA',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#38C172',
                      marginBottom: '4px'
                    }}>
                      {formatRecordCount(recordCount)}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: mode === 'dark' ? '#9aa8bd' : '#8B9CB5',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Registros
                    </div>
                  </div>
                  
                  <div style={{
                    padding: '12px',
                    backgroundColor: mode === 'dark' ? '#151a21' : '#F5F7FA',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#29B5E8',
                      marginBottom: '4px'
                    }}>
                      {columnCount || 'N/A'}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: mode === 'dark' ? '#9aa8bd' : '#8B9CB5',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Columnas
                    </div>
                  </div>
                  
                  <div style={{
                    padding: '12px',
                    backgroundColor: mode === 'dark' ? '#151a21' : '#F5F7FA',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: categoryColor,
                      marginBottom: '4px'
                    }}>
                      {source.type === 'mysql' ? 'SQL' : source.type === 'google_drive' ? 'GD' : source.type.toUpperCase()}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: mode === 'dark' ? '#9aa8bd' : '#8B9CB5',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Tipo
                    </div>
                  </div>
                </div>
                
                {/* Badge de categoría */}
                <div style={{ marginBottom: '16px' }}>
                  <span style={{
                    ...styles.categoryBadge,
                    backgroundColor: `${categoryColor}20`,
                    color: categoryColor
                  }}>
                    {category}
                  </span>
                </div>
                
                {/* Acciones */}
                {!source.is_hardcoded && (
                  <div style={{ display: 'flex', gap: '8px', paddingTop: '12px', borderTop: `1px solid ${mode === 'dark' ? '#2d3748' : '#E2E8F0'}` }}>
                    <button
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: mode === 'dark' ? '#2d3748' : '#E2E8F0',
                        color: mode === 'dark' ? '#f6f8fb' : '#1A202C',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      onClick={() => onEdit(source)}
                      disabled={loading}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#29B5E8';
                        e.currentTarget.style.color = '#FFFFFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = mode === 'dark' ? '#2d3748' : '#E2E8F0';
                        e.currentTarget.style.color = mode === 'dark' ? '#f6f8fb' : '#1A202C';
                      }}
                    >
                      ✏️ Editar
                    </button>
                    <button
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        color: '#EF4444',
                        fontSize: '13px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      onClick={() => onDelete(source.id)}
                      disabled={loading}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#EF4444';
                        e.currentTarget.style.color = '#FFFFFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                        e.currentTarget.style.color = '#EF4444';
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          
          {filteredSources.length === 0 && (
            <div style={{
              gridColumn: '1 / -1',
              padding: '48px 24px',
              textAlign: 'center',
              color: mode === 'dark' ? '#9aa8bd' : '#8B9CB5',
              fontSize: '14px'
            }}>
              🔍 No se encontraron fuentes que coincidan con los filtros
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DataSourceList;

